import type { DockStatus, DockPresence } from '@/types/dock';

/**
 * What the COP says about a dock: its state, where it is in a launch or a landing, whether a
 * drone is sitting in it, and what the box can do.
 *
 * THE WORD "TRAY" IS RETIRED HERE (#505 T8).
 *
 * The product called the same physical thing three names on one screen: a control row named
 * `Tray` in the dock sidebar, `Bay is open · launch` in the launch overlay, and a
 * `Takeoff phase` section that was really about the same door. `lib/dock-bays.ts` even talks
 * about "two trays" where it means two bays. They are one thing to an operator - a lid that
 * opens over a slot - so the product says **bay**, once. `TRAY_OPEN` and friends stay as wire
 * constant names in `lib/dock-status.ts`; only what a person reads changes.
 *
 * ONE CARVE-OUT, ADDED 2026-09-05. The dock sidebar's `Takeoff phase` / `Landing phase` rows and
 * the four buttons under them are back, named for their wire verbs (`Pre-takeoff`, `Post-land`).
 * They are not a name for the door: they are the board's lifecycle commands, pressed to exercise
 * the hardware against an MQTT log. The reasoning is on `DOCK_COMMAND_LABEL` in
 * `lib/copy/commands.ts`. "bay" still wins everywhere it describes the physical thing, which is
 * everything in THIS file.
 *
 * CASE. Every consumer of this file renders through a CSS `uppercase` class - `DockSidebar`
 * for the status badge and the phase, `DockBayRow` for presence, `DockPanel` for its compact
 * badge - so these strings are sentence case in source and unchanged on screen. The exception
 * is `emergency`, which is a critical condition and shouts in the string itself, per
 * `docs/operational-standards.md`.
 */

/**
 * The dock's overall state, for the sidebar badge.
 *
 * Keys are the backend enum (`deriveStatusEnum`: emergency > tray > swap > charging > ready)
 * and do not change.
 */
export const DOCK_STATUS_LABEL: Readonly<Record<DockStatus, string>> = {
  /** Was `AWAITING CONNECTION`, which read as though the operator had to do something. */
  awaiting_connection: 'Waiting for the dock',
  offline: 'Offline',
  /** Critical, so it keeps its capitals in the string rather than borrowing the CSS. */
  emergency: 'EMERGENCY',
  tray_open: 'Bay open',
  tray_moving: 'Bay moving',
  /** Was `SWAPPING`, which never said what was being swapped. */
  swapping: 'Swapping battery',
  charging: 'Charging',
  ready: 'Ready',
};

/**
 * The same states, short, for `DockPanel`'s compact badge in a roster row.
 *
 * A second map rather than a truncation, because the two surfaces have genuinely different
 * room and the short forms must still be words. `E-STOP` is gone: it is a piece of hardware
 * jargon, and the sidebar already calls the same state EMERGENCY.
 */
export const DOCK_STATUS_SHORT: Readonly<Record<DockStatus, string>> = {
  awaiting_connection: 'Waiting',
  offline: 'Offline',
  emergency: 'EMERGENCY',
  tray_open: 'Bay open',
  tray_moving: 'Bay moving',
  swapping: 'Swapping',
  charging: 'Charging',
  ready: 'Ready',
};

/**
 * Where the dock is in a launch or a landing.
 *
 * Was `PREPARING FOR TAKEOFF` / `READY FOR TAKEOFF` / `TAKEOFF IN PROGRESS` and the landing
 * equivalents - six labels that named a workflow rather than a thing happening. What the dock
 * is actually doing while "preparing" is opening its bay, which is the one part an operator can
 * see from across the pad, so that is what it now says.
 */
export const DOCK_PHASE_LABEL = {
  preparingTakeoff: 'Opening the bay',
  readyTakeoff: 'Ready to take off',
  takingOff: 'Taking off',
  preparingLanding: 'Opening the bay to land',
  readyLanding: 'Ready to land',
  landing: 'Landing',
} as const;

/** No phase reported. The dock contract makes this the normal case today. */
export const DOCK_PHASE_UNKNOWN = 'No phase reported';

/**
 * The bay door's position, from the board's `tray` byte.
 *
 * 2 and 3 are transitional and must never read as open; the words keep that distinction
 * because "Opening" and "Closing" are plainly not "Open".
 */
export const BAY_POSITION_LABEL = {
  closed: 'Closed',
  open: 'Open',
  opening: 'Opening',
  closing: 'Closing',
} as const;

/** A byte the board sent that we have no word for. Names the number so it can be reported. */
export function bayPositionUnknown({ value }: { value: number }): string {
  return `Unknown (${value})`;
}

/** Whether the bay's drone is sitting in the dock. */
export const PRESENCE_LABEL: Readonly<Record<DockPresence, string>> = {
  in_dock: 'In the dock',
  not_in_dock: 'Not in the dock',
  unknown: 'Unknown',
};

/**
 * The drone is up, and how much the claim can be trusted.
 *
 * "Link weak" and "Link lost" are the same two words `lib/copy/status.ts` uses on the map chip
 * for the same two conditions - the point of a single copy module is that a dock row and a map
 * marker cannot invent separate vocabularies for one fact.
 */
export const AIRBORNE_LABEL = {
  flying: 'In the air',
  linkWeak: 'In the air, link weak',
  linkLost: 'In the air, link lost',
} as const;

/**
 * What the box can do. Was `DUAL-DRONE DOCK · CHARGER · BATTERY SWITCH`.
 *
 * The first part is capitalised and the rest are not, because `dockTypeLabel` joins them into
 * one sentence: "Two-drone dock, charger, battery swap".
 */
export const DOCK_CAPABILITY = {
  dual: 'Two-drone dock',
  basic: 'Basic dock',
  charger: 'charger',
  batterySwap: 'battery swap',
} as const;

/**
 * The dock labels allowed to shout, for the guard test's case rule.
 *
 * Tagged rather than inferred from the string: an edit that accidentally lower-cases a
 * critical condition, or capitalises a routine one, has to fail rather than pass quietly.
 */
export const CRITICAL_DOCK_LABELS: ReadonlySet<string> = new Set([
  DOCK_STATUS_LABEL.emergency,
  DOCK_STATUS_SHORT.emergency,
]);

/**
 * `DockPanel`'s word for the drone assigned to a dock.
 *
 * `AIRBORNE` becomes "In the air" to match every other surface. The `idle` case deliberately
 * stays a weak word: an earlier version said "LANDED", which is a stronger claim than any
 * surface is now permitted to make about a drone that has not said so itself (#338).
 */
export const DOCK_SLOT_LABEL = {
  unassigned: 'No drone assigned',
  unavailable: 'Drone unavailable',
  airborne: 'In the air',
  idle: 'On the pad',
} as const;

/**
 * Why the relief's dock cannot be commanded yet, for the coverage panel's timing breakdown
 * (stage 5 W1, `src/services/dock-time-to-ready.ts`).
 *
 * A SECOND MAP RATHER THAN A REUSE OF `DOCK_STATUS_LABEL`, because these answer a different
 * question. The status badge says what the box IS; this says what it means for a launch that has
 * not happened yet, and three of the eight carry no timeable wait at all. Where the two overlap
 * the words are deliberately identical - "Swapping battery" is the string #505 T8 settled on, and
 * an operator watching the dock sidebar and this panel during one rotation must not read two
 * words for one machine state.
 *
 * `emergency` keeps its capitals here for the same reason it does above.
 */
export const DOCK_READY_BASIS_LABEL = {
  ready: 'Ready',
  vendor_swap: 'Swapping battery',
  modelled_charge: 'Charging',
  no_pack: 'No charged battery',
  emergency: 'EMERGENCY',
  offline: 'Offline',
  awaiting_connection: 'Waiting for the dock',
  unknown: 'Not reported',
} as const;

/**
 * The bases whose zero is an ABSENCE rather than a measurement, so the panel can flag them.
 *
 * Mirrors `isBoundedReadiness` on the server. Tagged rather than inferred from the seconds,
 * because a `0` from a ready dock and a `0` from a dock with an empty magazine are the same
 * number and opposite facts.
 */
export const UNBOUNDED_DOCK_READY_BASES: ReadonlySet<string> = new Set([
  'no_pack', 'emergency', 'offline', 'awaiting_connection', 'unknown',
]);

/**
 * The dock pin check (docs/PLANS/dock-geoposition): the dock's pin against where its own aircraft
 * park, as `lib/dock-position-check.ts` maps it.
 *
 * ONE NOUN AND ONE ACTION (D14). The thing is the "pin" on every surface - never "position" in one
 * place and "dock position" in another - and the fix is always "in the dock editor". The server's
 * alert and launch advisory use the same words.
 *
 * Badge labels render through `uppercase`, so they are sentence case here like everything else in
 * this file. `compact*` are the fleet table's, whose column is already headed Position. `*Hint` is the
 * short line a table row can afford; `*Detail` is the full sentence the COP dock panel shows. A drift
 * distance is rounded on a badge and exact in a sentence.
 */
export const DOCK_POSITION_COPY = {
  ok: 'Pin ok',
  drift: ({ distanceM }: { distanceM: number }): string => `Pin drift ${Math.round(distanceM)} m`,
  noPin: 'No pin',
  notChecked: 'Pin not checked',

  compactOk: 'Ok',
  compactDrift: ({ distanceM }: { distanceM: number }): string => `Drift ${Math.round(distanceM)} m`,
  compactNoPin: 'No pin',

  driftHint: ({ distanceM, direction }: { distanceM: number; direction: string }): string =>
    `Move pin ${distanceM} m ${direction}`,
  noPinHint: 'Set the pin in the dock editor',

  okDetail: ({ distanceM, windows, warnM }: { distanceM: number; windows: number; warnM: number }): string =>
    `The pin matches where this dock's aircraft park: ${distanceM} m apart over the last ${windows} landings `
    + `and takeoffs. A warning starts at ${warnM} m.`,
  driftDetail: ({ distanceM, direction, windows, clearM }: {
    distanceM: number; direction: string; windows: number; clearM: number;
  }): string =>
    `Aircraft have been parking ${distanceM} m ${direction} of the pin (last ${windows} landings and takeoffs). `
    + `Returns and landings aim at the pin - move it ${distanceM} m ${direction} in the dock editor. `
    + `The warning clears once the pin is within ${clearM} m.`,
  noPinDetail: 'This dock has no pin, so landing checks have no dock to measure against, and a return with no '
    + 'home point falls back to the aircraft\'s own return-to-home. Set the pin in the dock editor.',
  noPinWithSuggestion: 'This dock has no pin, so landing checks have no dock to measure against. Its aircraft '
    + 'have been parking at one spot - use that spot in the dock editor.',
  notCheckedDetail: ({ windows }: { windows: number }): string =>
    `Not enough recent landings and takeoffs to check the pin yet (${windows} so far).`,
  unknownDetail: 'No recent pin check is available.',
} as const;

/**
 * The dock editor's pin panel (docs/PLANS/dock-geoposition D15). Same noun as the check above - the
 * "pin" - so the operator who followed "move it in the dock editor" finds the thing they were told
 * to move.
 */
export const DOCK_PIN_EDITOR_COPY = {
  heading: 'Dock pin',
  required: 'Required',
  explainer: 'Where the dock stands. Returns and landings aim at this pin, so place it on the dock itself.',
  useMyLocation: 'Use my location',
  locating: 'Locating...',
  backToSaved: 'Back to saved pin',
  coordinatesLabel: 'Coordinates (lat, lng)',
  coordinatesPlaceholder: '31.764461, 34.988816 - press Enter',
  mapHint: 'Or zoom the map in until you can see the dock, then click it or drag the pin onto it.',
  problem: {
    missing: 'Place the pin to continue - use your location, paste coordinates, or click the map.',
    unreadable: 'Could not read that. Paste latitude then longitude, like 31.764461, 34.988816.',
    out_of_range: 'Latitude must be between -90 and 90, and longitude between -180 and 180.',
    null_island: '0, 0 is what a GPS with no fix reports, not a dock position. Place the pin on the dock.',
  },
  myLocationUnsupported: 'This browser cannot share its location. Place the pin on the map instead.',
  myLocationDenied: 'Location access was refused. Place the pin on the map instead.',
  myLocationFailed: 'Your location could not be read. Place the pin on the map instead.',
  myLocationSuperseded: 'Your location arrived after the pin was moved, so it was not used.',
  myLocationTooCoarse: ({ accuracyM, maxM }: { accuracyM: number; maxM: number }): string =>
    `Your location is only accurate to ${Math.round(accuracyM)} m - a dock pin needs ${maxM} m or better. `
    + 'This device may have no GPS. Place the pin on the map instead.',
  myLocationPlaced: ({ accuracyM }: { accuracyM: number }): string =>
    `Set from this device's location (within ${Math.round(accuracyM)} m). Check the pin sits on the dock in the imagery.`,
  farFromPrevious: ({ distanceKm }: { distanceKm: number }): string =>
    `That is ${distanceKm} km from the previous pin. Check that latitude comes first.`,
  placed: {
    map: 'Placed on the map.',
    typed: 'Typed in.',
    my_location: 'Set from this device\'s location.',
    suggestion: 'Moved to where the aircraft park.',
  },
  notSavedUntil: ({ saveLabel }: { saveLabel: string }): string => `Not saved until you press ${saveLabel}.`,
  suggestionTitle: 'Where the aircraft park',
  suggestionDrift: ({ distanceM, direction, windows }: { distanceM: number; direction: string; windows: number }): string =>
    `The aircraft parked ${distanceM} m ${direction} of the saved pin over the last ${windows} landings and takeoffs `
    + '(GPS, good to a few metres). If the imagery shows the dock there, move the pin.',
  suggestionNoPin: ({ windows }: { windows: number }): string =>
    `No pin yet. The aircraft parked at one spot over the last ${windows} landings and takeoffs (GPS, good to a few metres).`,
  movePinHere: 'Move pin to this spot',
  suggestionApplied: 'The pin is at this spot.',
  saveBlocked: {
    name: 'Name the dock to save.',
    topic: 'Set the MQTT topic prefix to save.',
    draft: 'Press Enter in the coordinates field, or clear it, to save.',
    pin: 'Place the dock pin to save.',
    pinInvalid: 'Fix the dock pin to save.',
  },
} as const;
